export const M4_UNIT_3 = {
  id: "m4-u3-present-continuous",
  title: "Unidad 3: What's Happening Now?",
  topic: "Master present continuous! Learn to describe what you're wearing right now and what's happening at this moment. Essential for real-time conversations, describing situations, and talking about temporary actions!",
  iconType: "shirt",
  grammarImage: "/grammar/grammar-m4-u3.jpg",
  grammarText: "Present Continuous: I am/He is/They are + verb-ING. Usado para acciones EN ESTE MOMENTO o temporales. Ejemplo: 'I am wearing a jacket' (ahora), 'She is working today' (temporal).",
  
  sets: [
    // SET 1: Basic Structure - I am wearing
    {
      id: "m4-u3-set1",
      title: "Set 1: I'm Wearing...",
      questions: [
        {
          type: "mc",
          q: "Right now: 'I ___ a blue shirt.' (wear)",
          options: ["am wearing", "wear", "wearing"],
          a: "am wearing",
          tip: "Present continuous = AM/IS/ARE + verb-ING. 'I am wearing' = lo que traes puesto AHORA."
        },
        {
          type: "input",
          q: "Complete: 'I ___ jeans today.' (wear)",
          a: "am wearing"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy usando una chaqueta'",
          words: ["I", "am", "wearing", "a", "jacket."],
          a: "I am wearing a jacket."
        },
        {
          type: "mc",
          q: "'I ___ my work uniform right now.'",
          options: ["am wearing", "wear", "wears"],
          a: "am wearing"
        },
        {
          type: "input",
          q: "Complete: 'I ___ black shoes.' (wear - now)",
          a: "am wearing"
        },
        {
          type: "mc",
          q: "Contraction: 'I am wearing' = '___'",
          options: ["I'm wearing", "Im wearing", "I wearing"],
          a: "I'm wearing",
          tip: "I am = I'm. Contraction muy común en conversación!"
        },
        {
          type: "order",
          q: "Ordena: 'Traigo puestos mis tenis nuevos'",
          words: ["I'm", "wearing", "my", "new", "sneakers."],
          a: "I'm wearing my new sneakers."
        },
        {
          type: "input",
          q: "Complete: 'Today I ___ a dress.' (wear)",
          a: "am wearing"
        },
        {
          type: "mc",
          q: "'I ___ a coat because it's cold.'",
          options: ["am wearing", "wear", "wearing"],
          a: "am wearing",
          successtip: "✓ AM WEARING! Reason given! 🧥 Because = explaining why you're wearing it NOW. Context matters!"
        },
        {
          type: "input",
          q: "Complete: 'I ___ a hat today.' (wear)",
          a: "am wearing"
        }
      ]
    },

    // SET 2: Third Person - He/She is wearing
    {
      id: "m4-u3-set2",
      title: "Set 2: Describing Others",
      questions: [
        {
          type: "mc",
          q: "'She ___ a red dress today.'",
          options: ["is wearing", "wears", "wearing"],
          a: "is wearing",
          tip: "He/She/It + IS wearing. Third person = IS (not am or are)."
        },
        {
          type: "input",
          q: "Complete: 'He ___ a suit for the interview.' (wear)",
          a: "is wearing"
        },
        {
          type: "order",
          q: "Ordena: 'Él está usando botas'",
          words: ["He", "is", "wearing", "boots."],
          a: "He is wearing boots."
        },
        {
          type: "mc",
          q: "'My boss ___ a tie today.'",
          options: ["is wearing", "are wearing", "am wearing"],
          a: "is wearing"
        },
        {
          type: "input",
          q: "Complete: 'She ___ her winter coat.' (wear)",
          a: "is wearing",
          successtip: "✓ WINTER COAT! Canadian essential! ❄️ Parka, winter jacket, down coat = survival gear!"
        },
        {
          type: "match",
          q: "Match subject to verb form:",
          pairs: [
            { left: "I", right: "am wearing" },
            { left: "He", right: "is wearing" },
            { left: "They", right: "are wearing" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "Contraction: 'He is wearing' = '___'",
          options: ["He's wearing", "Hes wearing", "He wearing"],
          a: "He's wearing"
        },
        {
          type: "order",
          q: "Ordena: 'Ella está usando un suéter azul'",
          words: ["She's", "wearing", "a", "blue", "sweater."],
          a: "She's wearing a blue sweater."
        },
        {
          type: "input",
          q: "Complete: 'My coworker ___ sandals today.' (wear)",
          a: "is wearing"
        },
        {
          type: "mc",
          q: "'The security guard ___ a uniform.'",
          options: ["is wearing", "are wearing", "am wearing"],
          a: "is wearing"
        }
      ]
    },

    // SET 3: Questions - What are you wearing?
    {
      id: "m4-u3-set3",
      title: "Set 3: Asking About Clothes",
      questions: [
        {
          type: "mc",
          q: "'___ you wearing a jacket?'",
          options: ["Are", "Is", "Do"],
          a: "Are",
          tip: "Questions: ARE/IS + subject + verb-ING? 'Are you wearing...?'"
        },
        {
          type: "input",
          q: "Complete: '___ she wearing jeans?' (question)",
          a: "Is"
        },
        {
          type: "order",
          q: "Ordena: '¿Qué estás usando?'",
          words: ["What", "are", "you", "wearing?"],
          a: "What are you wearing?"
        },
        {
          type: "mc",
          q: "'___ he wearing a tie today?'",
          options: ["Is", "Are", "Does"],
          a: "Is"
        },
        {
          type: "match",
          q: "Match question to answer:",
          pairs: [
            { left: "What are you wearing?", right: "I'm wearing jeans" },
            { left: "Is she wearing a coat?", right: "Yes, she is" },
            { left: "Are they wearing uniforms?", right: "No, they aren't" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: '___ they wearing safety vests?' (question)",
          a: "Are"
        },
        {
          type: "mc",
          q: "Short answer: 'Are you wearing boots?' - 'Yes, ___'",
          options: ["I am", "I do", "I'm"],
          a: "I am",
          tip: "Short answers: Yes, I am / No, I'm not. Use full form for 'yes', can contract for 'no'."
        },
        {
          type: "order",
          q: "Ordena: '¿Está usando su uniforme de trabajo?'",
          words: ["Is", "he", "wearing", "his", "work", "uniform?"],
          a: "Is he wearing his work uniform?"
        },
        {
          type: "input",
          q: "Complete: 'What ___ she wearing to the party?' (question)",
          a: "is"
        },
        {
          type: "mc",
          q: "ERROR: 'You wearing a hat?' - ¿Qué falta?",
          options: ["'Are' at the start", "'-ing' ending", "Subject"],
          a: "'Are' at the start",
          tip: "Questions need AM/IS/ARE! 'Are you wearing...?' ✓"
        }
      ]
    },

    // SET 4: Negatives
    {
      id: "m4-u3-set4",
      title: "Set 4: What You're NOT Wearing",
      questions: [
        {
          type: "mc",
          q: "'I ___ a coat today, it's warm.'",
          options: ["am not wearing", "not wearing", "don't wearing"],
          a: "am not wearing",
          tip: "Negative: AM/IS/ARE + NOT + verb-ING. 'I am not wearing' or 'I'm not wearing'."
        },
        {
          type: "input",
          q: "Complete: 'She ___ her glasses right now.' (not wear)",
          a: "is not wearing / isn't wearing"
        },
        {
          type: "order",
          q: "Ordena: 'No estoy usando zapatos formales hoy'",
          words: ["I'm", "not", "wearing", "formal", "shoes", "today."],
          a: "I'm not wearing formal shoes today."
        },
        {
          type: "mc",
          q: "'He ___ a uniform today, it's his day off.'",
          options: ["isn't wearing", "not wearing", "doesn't wearing"],
          a: "isn't wearing"
        },
        {
          type: "match",
          q: "Match to negative form:",
          pairs: [
            { left: "I am wearing", right: "I'm not wearing" },
            { left: "She is wearing", right: "She isn't wearing" },
            { left: "They are wearing", right: "They aren't wearing" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: 'We ___ jackets, it's hot!' (not wear)",
          a: "are not wearing / aren't wearing"
        },
        {
          type: "mc",
          q: "Contraction: 'They are not wearing' = '___'",
          options: ["They aren't wearing", "They not wearing", "They don't wearing"],
          a: "They aren't wearing"
        },
        {
          type: "order",
          q: "Ordena: 'Él no está usando su gorra hoy'",
          words: ["He", "isn't", "wearing", "his", "hat", "today."],
          a: "He isn't wearing his hat today.",
          successtip: "✓ HAT! In Quebec: tuque! 🇨🇦 Tuque = knitted winter hat, very Québécois word!"
        },
        {
          type: "input",
          q: "Complete: 'I ___ my work boots today.' (not wear)",
          a: "am not wearing / I'm not wearing"
        },
        {
          type: "mc",
          q: "ERROR: 'I don't wearing shoes' - Corrección:",
          options: ["I'm not wearing shoes", "I not wearing shoes", "I doesn't wearing shoes"],
          a: "I'm not wearing shoes",
          tip: "Continuous negative = AM/IS/ARE + not + -ING. NOT 'don't'! That's simple tense."
        }
      ]
    },

    // SET 5: Simple vs Continuous (Introduction)
    {
      id: "m4-u3-set5",
      title: "Set 5: Now vs Usually",
      questions: [
        {
          type: "mc",
          q: "Rutina general: 'I usually ___ jeans to work.' (always/general)",
          options: ["wear", "am wearing", "wears"],
          a: "wear",
          tip: "'Usually' = hábito = Present Simple. 'Right now/today' = Present Continuous."
        },
        {
          type: "input",
          q: "Ahora: 'But today I ___ a suit.' (wear - now)",
          a: "am wearing"
        },
        {
          type: "order",
          q: "Ordena: 'Usualmente uso tenis, pero hoy uso botas'",
          words: ["I", "usually", "wear", "sneakers,", "but", "today", "I'm", "wearing", "boots."],
          a: "I usually wear sneakers, but today I'm wearing boots.",
          successtip: "✓ CONTRAST! Usually = simple, Today = continuous! 📚 This is KEY difference! Routine vs now!"
        },
        {
          type: "match",
          q: "Match time word to tense:",
          pairs: [
            { left: "usually", right: "Present Simple" },
            { left: "right now", right: "Present Continuous" },
            { left: "today", right: "Present Continuous" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "'She always ___ a uniform, but today she ___ casual clothes.'",
          options: ["wears, is wearing", "is wearing, wears", "wears, wears"],
          a: "wears, is wearing"
        },
        {
          type: "input",
          q: "General: 'He ___ glasses.' (present simple - always)",
          a: "wears"
        },
        {
          type: "mc",
          q: "Now: 'But right now he ___ sunglasses.'",
          options: ["is wearing", "wears", "wearing"],
          a: "is wearing"
        },
        {
          type: "order",
          q: "Ordena: 'Normalmente trabajo en casa, pero hoy estoy trabajando en la oficina'",
          words: ["I", "normally", "work", "at", "home,", "but", "today", "I'm", "working", "at", "the", "office."],
          a: "I normally work at home, but today I'm working at the office.",
          tip: "Same pattern! Normally = simple, today = continuous. Works for ANY verb!"
        },
        {
          type: "input",
          q: "Complete contrast: 'I usually ___ the bus, but today I ___ my bike.' (take, ride)",
          a: "take, am riding"
        },
        {
          type: "mc",
          q: "'Every day I ___ at 7am, but today I ___ at 9am.'",
          options: ["start, am starting", "am starting, start", "start, start"],
          a: "start, am starting"
        }
      ]
    },

    // SET 6: Actions in Progress (Not just clothes)
    {
      id: "m4-u3-set6",
      title: "Set 6: What Are You Doing?",
      questions: [
        {
          type: "mc",
          q: "Right now: 'I ___ my lunch.' (eat)",
          options: ["am eating", "eat", "eats"],
          a: "am eating",
          tip: "Present continuous para acciones EN PROGRESO ahora. Not just clothes!"
        },
        {
          type: "input",
          q: "Complete: 'She ___ on the phone.' (talk)",
          a: "is talking"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy esperando el autobús'",
          words: ["I'm", "waiting", "for", "the", "bus."],
          a: "I'm waiting for the bus."
        },
        {
          type: "mc",
          q: "'He ___ a break right now.'",
          options: ["is taking", "takes", "take"],
          a: "is taking"
        },
        {
          type: "match",
          q: "Match action to continuous form:",
          pairs: [
            { left: "eat", right: "eating" },
            { left: "work", right: "working" },
            { left: "study", right: "studying" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: 'We ___ for our shift to start.' (wait)",
          a: "are waiting"
        },
        {
          type: "mc",
          q: "'What ___ you doing?' - 'I ___ for a job.'",
          options: ["are, am looking", "do, look", "are, look"],
          a: "are, am looking",
          successtip: "✓ JOB HUNTING! What are you doing? 💼 'I'm looking for work' = active search. Stay positive!"
        },
        {
          type: "order",
          q: "Ordena: '¿Qué estás haciendo ahora?'",
          words: ["What", "are", "you", "doing", "now?"],
          a: "What are you doing now?"
        },
        {
          type: "input",
          q: "Complete: 'They ___ their English right now.' (practice)",
          a: "are practicing"
        },
        {
          type: "mc",
          q: "'Sorry, I ___ to music, I can't hear you.'",
          options: ["am listening", "listen", "listening"],
          a: "am listening"
        }
      ]
    },

    // SET 7: Temporary Situations
    {
      id: "m4-u3-set7",
      title: "Set 7: Temporary Actions",
      questions: [
        {
          type: "mc",
          q: "'This week I ___ the night shift.' (temporary)",
          options: ["am working", "work", "works"],
          a: "am working",
          tip: "'This week/month/year' = temporary period = continuous, even if not RIGHT now."
        },
        {
          type: "input",
          q: "Complete: 'She ___ with her sister this month.' (stay/live)",
          a: "is staying"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy tomando clases de inglés este año'",
          words: ["I'm", "taking", "English", "classes", "this", "year."],
          a: "I'm taking English classes this year.",
          successtip: "✓ ESL! Education investment! 📚 'Taking classes' = temporary action with long-term benefits!"
        },
        {
          type: "mc",
          q: "'These days, many immigrants ___ remote work.'",
          options: ["are doing", "do", "does"],
          a: "are doing"
        },
        {
          type: "match",
          q: "Match time expression to tense:",
          pairs: [
            { left: "every day", right: "Simple" },
            { left: "this week", right: "Continuous" },
            { left: "right now", right: "Continuous" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: 'He ___ money for his citizenship application.' (save - temporary focus)",
          a: "is saving"
        },
        {
          type: "mc",
          q: "'This month I ___ double shifts to pay rent.'",
          options: ["am working", "work", "works"],
          a: "am working"
        },
        {
          type: "order",
          q: "Ordena: 'Ella está buscando un departamento este mes'",
          words: ["She's", "looking", "for", "an", "apartment", "this", "month."],
          a: "She's looking for an apartment this month."
        },
        {
          type: "input",
          q: "Complete: 'We ___ for our work permits.' (wait - ongoing process)",
          a: "are waiting",
          successtip: "✓ IMMIGRATION PROCESS! Patience! ⏳ Waiting for permits = stressful but temporary. It will come!"
        },
        {
          type: "mc",
          q: "'This year, I ___ to speak English better.'",
          options: ["am learning", "learn", "learns"],
          a: "am learning"
        }
      ]
    },

    // SET 8: Common Errors
    {
      id: "m4-u3-set8",
      title: "Set 8: Fixing Mistakes",
      questions: [
        {
          type: "mc",
          q: "ERROR: 'I wearing a jacket' - ¿Qué falta?",
          options: ["'am' (verb to be)", "'-s' ending", "Subject"],
          a: "'am' (verb to be)",
          tip: "Continuous NEEDS am/is/are! 'I wearing' ✗, 'I am wearing' ✓"
        },
        {
          type: "input",
          q: "Corrige: 'She wearing jeans' → 'She ___ wearing jeans.'",
          a: "is"
        },
        {
          type: "order",
          q: "Corrige: 'He working right now'",
          words: ["He", "is", "working", "right", "now."],
          a: "He is working right now."
        },
        {
          type: "mc",
          q: "ERROR: 'I am wear a coat' - Corrección:",
          options: ["I am wearing a coat", "I am wears a coat", "I wearing a coat"],
          a: "I am wearing a coat",
          tip: "After am/is/are, verb needs -ING! 'Am wearing' ✓, 'am wear' ✗"
        },
        {
          type: "match",
          q: "Match error to correction:",
          pairs: [
            { left: "I wearing", right: "I am wearing" },
            { left: "She is wear", right: "She is wearing" },
            { left: "They wearing", right: "They are wearing" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Corrige: 'He is works at Walmart' → 'He ___ at Walmart.' (permanent job)",
          a: "works",
          tip: "Permanent job = simple! 'He works' ✓. Continuous only for temporary/in progress."
        },
        {
          type: "mc",
          q: "ERROR: 'Do you wearing a hat?' - Corrección:",
          options: ["Are you wearing a hat?", "Is you wearing a hat?", "Do you wear a hat?"],
          a: "Are you wearing a hat?",
          tip: "Continuous questions use AM/IS/ARE, not DO/DOES! 'Are you wearing...?' ✓"
        },
        {
          type: "order",
          q: "Corrige: 'What you are doing?'",
          words: ["What", "are", "you", "doing?"],
          a: "What are you doing?",
          tip: "Question order: Question word + AM/IS/ARE + subject + -ING. 'What are you doing?' ✓"
        },
        {
          type: "input",
          q: "Corrige: 'I am study English' → 'I ___ English.'",
          a: "am studying"
        },
        {
          type: "mc",
          q: "'I ___ at Tim Hortons.' (permanent job)",
          options: ["work", "am working", "working"],
          a: "work",
          successtip: "✓ PERMANENT JOB! Simple! ☕ 'I work at' = your regular job. 'I'm working' = right now/temporary!"
        }
      ]
    },

    // SET 9: Real Situations
    {
      id: "m4-u3-set9",
      title: "Set 9: Everyday Contexts",
      questions: [
        {
          type: "mc",
          q: "Lost person description: 'I ___ for my friend. She ___ a red coat.'",
          options: ["am looking, is wearing", "look, wears", "am looking, wears"],
          a: "am looking, is wearing",
          tip: "Both happening now! 'Looking' = continuous, 'wearing' = continuous. Real emergency!"
        },
        {
          type: "input",
          q: "Video call: 'Can you see me? I ___ my hand.' (wave)",
          a: "am waving"
        },
        {
          type: "order",
          q: "Ordena: '¿Por qué estás usando tu abrigo? Hace calor'",
          words: ["Why", "are", "you", "wearing", "your", "coat?", "It's", "warm."],
          a: "Why are you wearing your coat? It's warm."
        },
        {
          type: "mc",
          q: "Store: 'Excuse me, I ___ this shirt. Where are the fitting rooms?'",
          options: ["am trying on", "try on", "trying on"],
          a: "am trying on",
          successtip: "✓ SHOPPING! Try on = probarse! 🛍️ 'Fitting room' = vestidor. 'Can I try this on?' = essential phrase!"
        },
        {
          type: "match",
          q: "Match situation to sentence:",
          pairs: [
            { left: "Security check", right: "I'm wearing a belt" },
            { left: "Cold weather", right: "I'm wearing my parka" },
            { left: "Video interview", right: "I'm wearing a suit" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Text to friend: 'I ___ at the entrance. I ___ a blue jacket.' (wait, wear)",
          a: "am waiting, am wearing"
        },
        {
          type: "mc",
          q: "Workplace: 'The manager ___ everyone to wear safety vests today.'",
          options: ["is asking", "asks", "ask"],
          a: "is asking"
        },
        {
          type: "order",
          q: "Ordena: 'Estamos esperando el autobús bajo la lluvia'",
          words: ["We're", "waiting", "for", "the", "bus", "in", "the", "rain."],
          a: "We're waiting for the bus in the rain.",
          successtip: "✓ CANADIAN WEATHER! Rain or shine! ☔ Always carry an umbrella or raincoat. Weather changes fast!"
        },
        {
          type: "input",
          q: "Family call: 'Look! Your daughter ___ her first steps!' (take)",
          a: "is taking"
        },
        {
          type: "mc",
          q: "Phone: 'I can't talk now, I ___ the subway.'",
          options: ["am taking", "take", "takes"],
          a: "am taking"
        }
      ]
    },

    // SET 10: Advanced Contexts
    {
      id: "m4-u3-set10",
      title: "Set 10: Complex Situations",
      questions: [
        {
          type: "mc",
          q: "'These days, I ___ two jobs and ___ English classes at night.'",
          options: ["am working, taking", "work, take", "am working, take"],
          a: "am working, taking",
          tip: "'These days' = temporary period = continuous for both actions!"
        },
        {
          type: "input",
          q: "Complete: 'While I ___ for my citizenship, I ___ as much as possible.' (wait, save)",
          a: "am waiting, am saving"
        },
        {
          type: "order",
          q: "Ordena: 'Mi esposa está trabajando de cajera mientras estudia contabilidad'",
          words: ["My", "wife", "is", "working", "as", "a", "cashier", "while", "she", "studies", "accounting."],
          a: "My wife is working as a cashier while she studies accounting.",
          successtip: "✓ SURVIVAL JOB! Credential recognition! 💪 Many professionals work 'survival jobs' while recertifying. Respect the hustle!"
        },
        {
          type: "match",
          q: "Match immigrant milestone to sentence:",
          pairs: [
            { left: "Job searching", right: "I'm sending resumes every day" },
            { left: "Language learning", right: "I'm taking ESL classes" },
            { left: "Family sponsorship", right: "I'm saving for their flights" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "'Right now I ___ minimum wage, but I ___ for better opportunities.'",
          options: ["am earning, am looking", "earn, look", "am earning, look"],
          a: "am earning, am looking"
        },
        {
          type: "input",
          q: "Complete: 'This year I ___ really hard to improve my English.' (work)",
          a: "am working"
        },
        {
          type: "mc",
          q: "'We ___ in a basement apartment now, but we ___ for a house.'",
          options: ["are living, are saving", "live, save", "are living, save"],
          a: "are living, are saving",
          successtip: "✓ HOUSING JOURNEY! Immigrant path! 🏠 Basement → apartment → house = typical progression. You'll get there!"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy aprendiendo a conducir porque necesito un carro para el trabajo'",
          words: ["I'm", "learning", "to", "drive", "because", "I", "need", "a", "car", "for", "work."],
          a: "I'm learning to drive because I need a car for work."
        },
        {
          type: "input",
          q: "Complete: 'My brother ___ with us until he finds his own place.' (stay)",
          a: "is staying"
        },
        {
          type: "mc",
          q: "Complete integration: 'I usually ___ jeans, but today I ___ a suit because I ___ for a promotion interview.'",
          options: ["wear, am wearing, am going", "am wearing, wear, go", "wear, wear, am going"],
          a: "wear, am wearing, am going",
          successtip: "✓ UNIT 3 COMPLETE! Continuous mastery! 🎯 You can now describe what's happening NOW, temporary situations, and contrast with routines! Simple vs Continuous = unlocked! 🎉"
        }
      ]
    }
  ],

  
// ===================================================================
// READING SET: Marco and Ana's Meeting
// ===================================================================
readingSet: {
  id: "m4-u3-reading",
  title: "Set 11: Reading Practice",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: Location & Description (preguntas 1-3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Waiting at Tim Hortons**

Hi! My name is Marco and I'm at the Tim Hortons on Queen Street right now. I'm waiting for my friend Ana. I'm wearing a black jacket and blue jeans, and I'm sitting near the window.`
    },
    {
      type: "mc",
      q: "¿Dónde está Marco?",
      options: ["At Tim Hortons on Queen Street", "On the subway", "At his house"],
      a: "At Tim Hortons on Queen Street",
      successtip: "✓ LOCATION!\nHe is at a very famous Canadian coffee shop! ☕🍩"
    },
    {
      type: "mc",
      q: "¿Qué está haciendo Marco ahora?",
      options: ["Waiting for his friend Ana", "Working", "Taking the subway"],
      a: "Waiting for his friend Ana",
      successtip: "✓ ACTION NOW!\nHe is waiting for his friend. ⌚🚶"
    },
    {
      type: "mc",
      q: "¿Qué está usando Marco?",
      options: ["Black jacket and blue jeans", "Red coat", "Work uniform"],
      a: "Black jacket and blue jeans",
      successtip: "✓ DESCRIPTION!\nBlack jacket and blue jeans. Good for recognition! 🧥👖"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: The Delay (preguntas 4-5)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Subway Problem**

Ana is usually very punctual, but today she's running late because the subway isn't working. While I'm waiting, I'm drinking a coffee and checking my phone.`
    },
    {
      type: "mc",
      q: "¿Por qué Ana llega tarde?",
      options: ["The subway isn't working", "She forgot", "She's working"],
      a: "The subway isn't working",
      successtip: "✓ TRANSPORT ISSUE!\nThe subway has a problem today. 🚇❌"
    },
    {
      type: "mc",
      q: "¿Qué está haciendo Marco mientras espera?",
      options: ["Drinking coffee and checking his phone", "Reading a book", "Working on his laptop"],
      a: "Drinking coffee and checking his phone",
      successtip: "✓ WAITING HABITS!\nCoffee and phone = typical waiting routine! ☕📱"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Routine vs. Today (preguntas 6-8)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**A Nice Day for a Walk**

I usually take the bus to meet Ana, but today I'm walking because it's a nice day. Ana just texted me - she's wearing a red coat and she's arriving in 5 minutes!`
    },
    {
      type: "mc",
      q: "¿Cómo llega Marco normalmente?",
      options: ["By bus", "Walking", "By subway"],
      a: "By bus",
      tip: "'Usually take the bus' = routine = present simple! 🚌",
      successtip: "✓ ROUTINE!\nYou identified his usual habit. 🎯"
    },
    {
      type: "mc",
      q: "¿Cómo está viajando Marco HOY?",
      options: ["Walking", "By bus", "By car"],
      a: "Walking",
      tip: "'Today I'm walking' = today's exception = present continuous! 🚶",
      successtip: "✓ EXCEPTION!\nToday is different because the weather is nice! ☀️"
    },
    {
      type: "mc",
      q: "¿De qué color es el abrigo de Ana?",
      options: ["Red", "Black", "Blue"],
      a: "Red",
      successtip: "✓ IDENTIFICATION!\nA red coat is easy to see in a crowd! 🧥🔴"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Grammar & Inference (preguntas 9-10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Summary**

In this story, Marco uses different tenses to explain his life. He uses the **Present Simple** for things that are always true or habits, and the **Present Continuous** for things happening right now.`
    },
    {
      type: "mc",
      q: "¿Cuántos tiempos verbales usa Marco en su texto?",
      options: ["Dos (present simple y continuous)", "Solo present simple", "Solo present continuous"],
      a: "Dos (present simple y continuous)",
      successtip: "✓ BOTH TENSES! Real conversation! 💬 Simple for routines ('usually take'), continuous for now ('I'm waiting'). Natural mix!"
    },
    {
      type: "mc",
      q: "Inferencia: ¿Por qué Marco describe lo que está usando?",
      options: ["Para que Ana lo pueda reconocer cuando llegue", "Porque está en una tienda", "Porque hace frío"],
      a: "Para que Ana lo pueda reconocer cuando llegue",
      tip: "Real situation! Describing clothes = identification. 'I'm wearing X' helps friends find you!",
      successtip: "✓ LOGIC!\nDescribing clothes is essential for meeting someone in a public place. 🤝"
    }
  ]
},
  // REVIEW SET
  reviewSet: {
    id: "m4-u3-review",
    title: "Set 12: Unit Review",
    category: "review",
    questions: [
      {
        type: "mc",
        q: "Present continuous se forma con:",
        options: ["AM/IS/ARE + verb-ING", "DO/DOES + verb", "Verb + -s"],
        a: "AM/IS/ARE + verb-ING"
      },
      {
        type: "match",
        q: "Match subject to continuous form:",
        pairs: [
          { left: "I", right: "am wearing" },
          { left: "She", right: "is wearing" },
          { left: "We", right: "are wearing" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "'Right now, I ___ my lunch.'",
        options: ["am eating", "eat", "eats"],
        a: "am eating"
      },
      {
        type: "input",
        q: "Question: '___ you wearing a jacket?' (question)",
        a: "Are"
      },
      {
        type: "mc",
        q: "Negative: 'She ___ her uniform today.'",
        options: ["isn't wearing", "doesn't wearing", "not wearing"],
        a: "isn't wearing"
      },
      {
        type: "mc",
        q: "Contraste: 'I usually ___ jeans, but today I ___ a suit.'",
        options: ["wear, am wearing", "am wearing, wear", "wear, wear"],
        a: "wear, am wearing",
        tip: "Usually = simple, today = continuous! Key difference!"
      },
      {
        type: "order",
        q: "Ordena: '¿Qué estás haciendo ahora?'",
        words: ["What", "are", "you", "doing", "now?"],
        a: "What are you doing now?"
      },
      {
        type: "mc",
        q: "'This week I ___ the night shift.' (temporary)",
        options: ["am working", "work", "works"],
        a: "am working"
      },
      {
        type: "input",
        q: "Corrige: 'He working now' → 'He ___ working now.'",
        a: "is"
      },
      {
        type: "mc",
        q: "Final integration: 'I ___ at a hospital, but this month I ___ extra shifts, so I ___ really tired.'",
        options: ["work, am working, am", "am working, work, am", "work, work, am"],
        a: "work, am working, am",
        successtip: "✓ UNIT 3 MASTERED! 🎉 Permanent job = simple, temporary extra = continuous, feeling now = continuous! You can now mix tenses naturally like a native speaker! Present continuous = UNLOCKED! 🇨🇦💪"
      }
    ]
  }
};
